import { AddressBlock } from "./AddressBlock";
import { clipDateYear } from "./dateUtils";
import { DateField } from "./DateField";
import { TitelSelect } from "./TitelSelect";

/**
 * Gesetzlicher Vertreter — appears when "Ich bin gesetzlich vertreten"
 * is confirmed. Becomes a 2nd Excel row for the Mandant.
 *
 * value shape:
 *  { anrede, titel, vorname, name, geburtsdatum,
 *    use_main_address, strasse, hausnummer, plz, ort, country_code,
 *    mobiltelefon, festnetz, fax, email }
 */
export const LegalRepSection = ({ value, onChange, searchPlaces }) => {
  const set = (k) => (v) => onChange({ ...value, [k]: v });

  const repAddress = {
    strasse: value.strasse || "",
    hausnummer: value.hausnummer || "",
    plz: value.plz || "",
    ort: value.ort || "",
    country_code: value.country_code || "DE",
  };
  const onRepAddressChange = (next) =>
    onChange({
      ...value,
      strasse: next.strasse,
      hausnummer: next.hausnummer,
      plz: next.plz,
      ort: next.ort,
      country_code: next.country_code,
    });

  return (
    <div className="space-y-5">
      <p className="text-[13px] text-[#505a74] leading-relaxed">
        Bitte tragen Sie die Daten des gesetzlichen Vertreters / Betreuers /
        Bevollmächtigten ein. Diese werden als <strong>zweite Zeile</strong> in
        der Mandantendatei erfasst.
      </p>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field
          id="rep-anrede"
          label="Anrede *"
          value={value.anrede}
          onChange={set("anrede")}
          placeholder="Herr / Frau"
        />
        <TitelSelect
          id="rep-titel"
          value={value.titel}
          onChange={set("titel")}
        />
        <Field
          id="rep-vorname"
          label="Vorname *"
          value={value.vorname}
          onChange={set("vorname")}
        />
        <Field
          id="rep-name"
          label="Name *"
          value={value.name}
          onChange={set("name")}
        />
        <DateField
          id="rep-geburtsdatum"
          label="Geburtsdatum"
          required
          value={value.geburtsdatum}
          onChange={set("geburtsdatum")}
          className="sm:col-span-2"
        />
      </div>

      <div className="pt-4 border-t border-[#e8e6df]">
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="form-input-rep-use-main-address"
            checked={value.use_main_address}
            onChange={(e) => set("use_main_address")(e.target.checked)}
            className="mt-1 w-4 h-4 accent-[#d90429]"
          />
          <span className="text-[14px] text-[#0a1128]">
            <strong>Adresse des Mandanten übernehmen.</strong>
            <span className="block text-[#505a74] text-[13px] mt-0.5">
              Deaktivieren, wenn der Vertreter unter einer anderen Anschrift
              erreichbar ist.
            </span>
          </span>
        </label>

        {!value.use_main_address && (
          <div className="mt-5 pl-7" data-testid="rep-address-block">
            <AddressBlock
              value={repAddress}
              onChange={onRepAddressChange}
              search={searchPlaces}
              idPrefix="form-rep-address"
              searchPlaceholder="Anschrift des gesetzlichen Vertreters"
            />
          </div>
        )}
      </div>

      <div className="pt-4 border-t border-[#e8e6df]">
        <div className="eyebrow text-[#505a74] mb-4">
          Kontaktdaten (optional)
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field
            id="rep-mobil"
            type="tel"
            label="Telefon"
            value={value.mobiltelefon}
            onChange={set("mobiltelefon")}
          />
          <Field
            id="rep-festnetz"
            type="tel"
            label="Festnetz"
            value={value.festnetz}
            onChange={set("festnetz")}
          />
          <Field
            id="rep-fax"
            type="tel"
            label="Fax"
            value={value.fax}
            onChange={set("fax")}
          />
          <Field
            id="rep-email"
            type="email"
            label="E-Mail"
            value={value.email}
            onChange={set("email")}
          />
        </div>
      </div>
    </div>
  );
};

const Field = ({ id, label, value, onChange, type = "text", placeholder = "", className = "" }) => {
  const isTel = type === "tel";
  return (
  <label className={`block ${className}`}>
    <span className="eyebrow text-[#505a74]">{label}</span>
    <input
      type={type}
      inputMode={isTel ? "tel" : undefined}
      pattern={isTel ? "[0-9+\\s\\-()/]*" : undefined}
      value={value || ""}
      data-testid={`form-input-${id}`}
      onChange={(e) => {
        const v = type === "date" ? clipDateYear(e.target.value) : e.target.value;
        onChange(v);
      }}
      max={type === "date" ? "9999-12-31" : undefined}
      placeholder={placeholder}
      className="mt-2 block w-full bg-white border border-[#e8e6df] rounded-sm px-4 py-3 text-[15px] focus:outline-none focus:border-[#0a1128] transition"
    />
  </label>
  );
};
